import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { PdpRespStorytellingText } from './PdpRespStorytellingText';
import { PdpSharedStorytellingRelatedContent } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingRelatedContent';
import { PdpSharedStorytellingMainMedia } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingMainMedia';
import { PdpSharedStorytellingShopAllLink } from '../PdpSharedStorytellingComponents/PdpSharedStorytellingShopAllLink';

import { getSrcsetString } from '../../utils/srcSet';

import { PdpRespStorytellingTab_storyTellingTab } from './__generated__/PdpRespStorytellingTab_storyTellingTab.graphql';

import classnames from 'classnames';

import styles from './PdpRespStorytellingModule.scss';

type Props = {
    storyTellingTab: PdpRespStorytellingTab_storyTellingTab;
};

const PdpRespStorytellingTabComponent: FC<Props> = ({ storyTellingTab }) => {
    const { supplementaryEditorial, smallImage, mainImage, mainVideo } = storyTellingTab;
    const hasEditorial = !!supplementaryEditorial?.totalResults;
    const showThirdColumn = hasEditorial;

    const smallImageAndArticles = smallImage && showThirdColumn;
    const smallImageAndText = smallImage && !showThirdColumn;
    const largeImageAndArticles = (mainImage || mainVideo) && showThirdColumn;
    const largeImageAndText = (mainImage || mainVideo) && !showThirdColumn;

    function getColumnClass(columnClass: string): string {
        return classnames(columnClass, {
            [styles.smallImageAndArticles]: smallImageAndArticles,
            [styles.smallImageAndText]: smallImageAndText,
            [styles.largeImageAndArticles]: largeImageAndArticles,
            [styles.largeImageAndText]: largeImageAndText,
        });
    }

    return (
        <div className={styles.tabContainer}>
            <div className={styles.innerTabContainer}>
                {smallImage && (
                    <div className={`${styles.imageColumn} ${styles.smallImageCol}`}>
                        <div className={styles.smallImageWrapper}>
                            <img
                                className={styles.smallImage}
                                src={smallImage}
                                srcSet={getSrcsetString(smallImage)}
                                sizes="20vw"
                                alt=""
                            />
                        </div>
                        <PdpSharedStorytellingShopAllLink storyTellingTab={storyTellingTab} />
                    </div>
                )}
                <div className={getColumnClass(styles.textCol)}>
                    <PdpRespStorytellingText storyTellingTab={storyTellingTab} />
                </div>
                {(mainImage || mainVideo) && (
                    <div className={`${styles.imageColumn} ${getColumnClass(styles.mainImageCol)}`}>
                        <PdpSharedStorytellingMainMedia storyTellingTab={storyTellingTab} />
                    </div>
                )}
                {showThirdColumn ? (
                    <div className={styles.supplementaryArticlesColumn}>
                        <PdpSharedStorytellingRelatedContent storyTellingTab={storyTellingTab} />
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export const PdpRespStorytellingTab = createFragmentContainer(PdpRespStorytellingTabComponent, {
    storyTellingTab: graphql`
        fragment PdpRespStorytellingTab_storyTellingTab on StoryTellingTab {
            ...PdpRespStorytellingText_storyTellingTab
            ...PdpSharedStorytellingRelatedContent_storyTellingTab
            ...PdpSharedStorytellingMainMedia_storyTellingTab
            ...PdpSharedStorytellingShopAllLink_storyTellingTab
            smallImage
            mainImage
            mainVideo
            supplementaryEditorial {
                totalResults
            }
        }
    `,
});
